/**
 * Logo Carousel — Dawn Theme Asset
 * logo-carousel.js
 *
 * Features:
 * - CSS-driven auto-scroll (infinite loop via cloned slides)
 * - Manual prev/next with smooth snap
 * - Touch / drag support
 * - Keyboard navigation (arrow keys)
 * - Respects prefers-reduced-motion
 * - Pause-on-hover
 * - Dot navigation sync
 * - Shopify section events (reload on block add/remove/reorder)
 *
 * Fixes:
 * - Auto-scroll resumes after touch/drag interaction on mobile
 * - Dot navigation correctly switches between animation mode and
 *   transform mode, then resumes auto-scroll after a delay
 * - _resume() is now always called at the end of touch/drag interactions
 * - Animation is properly reset (via node replacement trick) when
 *   resuming from a manual position, avoiding the two-system conflict
 */

(function () {
  'use strict';

  class LogoCarousel {
    constructor(section) {
      this.section     = section;
      this.wrapper     = section.querySelector('.logo-carousel__track-wrapper');
      this.viewport    = section.querySelector('.logo-carousel__viewport');
      this.track       = section.querySelector('.logo-carousel__track');
      this.btnPrev     = section.querySelector('.logo-carousel__btn--prev');
      this.btnNext     = section.querySelector('.logo-carousel__btn--next');
      this.dots        = section.querySelectorAll('.logo-carousel__dot');
      this.slides      = section.querySelectorAll('.logo-carousel__slide:not(.logo-carousel__slide--clone)');
      this.allSlides   = section.querySelectorAll('.logo-carousel__slide');

      if (!this.track || !this.slides.length) return;

      this.autoplay      = this.wrapper.dataset.autoplay !== 'false';
      this.speed         = parseInt(this.wrapper.dataset.speed, 10) || 40;
      this.pauseOnHover  = this.wrapper.dataset.pauseOnHover !== 'false';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.currentIndex  = 0;
      this.slideCount    = this.slides.length;
      this.isDragging    = false;
      this.dragStartX    = 0;
      this.dragCurrentX  = 0;

      // Track whether we are in manual (transform) mode vs animation mode
      this._manualMode   = false;
      // Timer to resume autoplay after manual interaction
      this._resumeTimer  = null;

      this._init();
    }

    _init() {
      this._applyCSSVars();
      this._setupAutoScroll();
      this._bindEvents();
      this._updateDots();
    }

    _applyCSSVars() {
      // Slide widths are set via Liquid-generated <style> block.
    }

    _setupAutoScroll() {
      if (this.reducedMotion) {
        this.track.style.animationPlayState = 'paused';
        return;
      }
      if (this.autoplay) {
        this._enterAnimationMode();
      } else {
        this.track.style.animationPlayState = 'paused';
        this._goTo(0);
      }
    }

    /**
     * Switch to CSS animation mode (auto-scroll).
     * Uses the node-clone trick to force a CSS animation restart
     * after we've been in manual transform mode.
     */
    _enterAnimationMode() {
      this._manualMode = false;

      // Remove any inline transform so the animation takes over cleanly
      this.track.style.transition = 'none';
      this.track.style.transform  = '';

      // Force animation restart by briefly toggling animationPlayState
      // The clone trick is the most reliable cross-browser way to do this.
      this.track.style.animation = 'none';
      // Trigger reflow
      void this.track.offsetWidth; // eslint-disable-line no-void
      this.track.style.animation  = '';
      this.track.style.animationDuration  = `${this.speed}s`;
      this.track.style.animationPlayState = 'running';
    }

    _pause() {
      this.track.style.animationPlayState = 'paused';
    }

    /**
     * Resume auto-scroll. If we are in manual/transform mode we need
     * to re-enter animation mode so the two systems don't conflict.
     */
    _resume() {
      if (!this.autoplay || this.reducedMotion) return;
      if (this._manualMode) {
        this._enterAnimationMode();
      } else {
        this.track.style.animationPlayState = 'running';
      }
    }

    /**
     * Schedule auto-scroll to resume after manual interactions.
     * Clears any pending resume so rapid taps don't stack timers.
     */
    _scheduleResume(delay = 3000) {
      if (!this.autoplay || this.reducedMotion) return;
      clearTimeout(this._resumeTimer);
      this._resumeTimer = setTimeout(() => {
        if (!this.isDragging) {
          this._resume();
        }
      }, delay);
    }

    /** Snap-scroll to a real slide index (non-cloned half) */
    _goTo(index) {
      // Cancel any pending auto-resume while we navigate
      clearTimeout(this._resumeTimer);

      // Switch to manual/transform mode
      this._manualMode = true;
      this._pause();

      // Remove the CSS animation so transform drives position
      this.track.style.animation = 'none';

      // Wrap index
      this.currentIndex = ((index % this.slideCount) + this.slideCount) % this.slideCount;

      const slideWidth = this._slideWidth();
      const offset     = -(this.currentIndex * slideWidth);

      this.track.style.transition = 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)';
      this.track.style.transform  = `translateX(${offset}px)`;

      this._updateDots();

      // Resume auto-scroll after the user has had time to see their selection
      this._scheduleResume(3000);
    }

    _slideWidth() {
      return this.slides[0] ? this.slides[0].offsetWidth : 0;
    }

    _visibleSlides() {
      const sw = this._slideWidth();
      return sw ? Math.round(this.viewport.offsetWidth / sw) || 1 : 1;
    }

    _updateDots() {
      this.dots.forEach((dot, i) => {
        const active = i === this.currentIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active.toString());
      });
    }

    _bindEvents() {
      // Hover pause/resume (desktop only — touchstart handles mobile)
      if (this.pauseOnHover) {
        this.wrapper.addEventListener('mouseenter', () => {
          if (!this._manualMode) this._pause();
        });
        this.wrapper.addEventListener('mouseleave', () => {
          if (!this._manualMode) this._resume();
        });
      }

      // Prev / Next buttons
      if (this.btnPrev) {
        this.btnPrev.addEventListener('click', () => {
          this._goTo(this.currentIndex - this._visibleSlides());
        });
      }
      if (this.btnNext) {
        this.btnNext.addEventListener('click', () => {
          this._goTo(this.currentIndex + this._visibleSlides());
        });
      }

      // Dots — fixed: tap on mobile now works because we properly handle
      // the animation/transform conflict and schedule a resume afterward
      this.dots.forEach(dot => {
        dot.addEventListener('click', () => {
          this._goTo(parseInt(dot.dataset.index, 10));
        });
        // Ensure touch taps on dots are treated as clicks on mobile
        dot.addEventListener('touchend', e => {
          e.preventDefault(); // prevent ghost click delay
          this._goTo(parseInt(dot.dataset.index, 10));
        });
      });

      // Keyboard on viewport
      this.viewport.addEventListener('keydown', e => {
        if (e.key === 'ArrowLeft')  { e.preventDefault(); this._goTo(this.currentIndex - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this._goTo(this.currentIndex + 1); }
      });

      // ── Touch drag ────────────────────────────────────────────────────────
      this.track.addEventListener('touchstart', e => {
        this._dragStart(e.touches[0].clientX);
      }, { passive: true });

      this.track.addEventListener('touchmove', e => {
        this._dragMove(e.touches[0].clientX);
      }, { passive: true });

      // Fixed: _dragEnd now calls _scheduleResume so autoplay comes back
      this.track.addEventListener('touchend', () => this._dragEnd());
      this.track.addEventListener('touchcancel', () => this._dragEnd());

      // ── Mouse drag ────────────────────────────────────────────────────────
      this.track.addEventListener('mousedown', e => {
        e.preventDefault();
        this._dragStart(e.clientX);
      });
      window.addEventListener('mousemove', e => {
        if (this.isDragging) this._dragMove(e.clientX);
      });
      window.addEventListener('mouseup', () => {
        if (this.isDragging) this._dragEnd();
      });
    }

    _dragStart(x) {
      clearTimeout(this._resumeTimer);

      // Switch to manual mode so we can apply transform during drag
      this._manualMode = true;
      this._pause();
      this.track.style.animation = 'none';

      this.isDragging   = true;
      this.dragStartX   = x;
      this.dragCurrentX = x;

      this.track.classList.add('logo-carousel__track--dragging');
      this.track.style.transition = 'none';
    }

    _dragMove(x) {
      if (!this.isDragging) return;
      this.dragCurrentX = x;
      // Live visual feedback during drag
      const slideWidth = this._slideWidth();
      const base       = -(this.currentIndex * slideWidth);
      const delta      = x - this.dragStartX;
      this.track.style.transform = `translateX(${base + delta}px)`;
    }

    _dragEnd() {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.track.classList.remove('logo-carousel__track--dragging');

      const delta     = this.dragCurrentX - this.dragStartX;
      const threshold = 50;

      if (Math.abs(delta) > threshold) {
        this._goTo(delta < 0
          ? this.currentIndex + this._visibleSlides()
          : this.currentIndex - this._visibleSlides()
        );
      } else {
        // Snap back to current slide, then schedule resume
        this._goTo(this.currentIndex);
      }

      // _goTo already schedules the resume, but make sure isDragging
      // is false before the timer fires (it is, set above).
    }

    destroy() {
      clearTimeout(this._resumeTimer);
    }
  }

  function initAll() {
    document.querySelectorAll('.logo-carousel').forEach(section => {
      if (section._lcInstance) return;
      section._lcInstance = new LogoCarousel(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', e => {
    const s = e.target.querySelector('.logo-carousel');
    if (s) { s._lcInstance = new LogoCarousel(s); }
  });
  document.addEventListener('shopify:section:unload', e => {
    const s = e.target.querySelector('.logo-carousel');
    if (s && s._lcInstance) { s._lcInstance.destroy(); delete s._lcInstance; }
  });
  document.addEventListener('shopify:block:select', e => {
    const s = e.target.closest('.logo-carousel');
    if (s && s._lcInstance) { /* optionally scroll to block */ }
  });
})();